import React, { useState, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import moment from "moment";
import { useAuth } from "../../../Common/Configurations/AuthContext";
import config from "../../../Common/Configurations/APIConfig";
import apiService from "../../../Common/Configurations/APIService";
import DefaultData from "../../../CompanyData/DefaultData";
import Header from "../../../Common/Layout/Header";
import { FaShoppingBag, FaUtensils } from "react-icons/fa";
import { RiAlertLine, RiMoneyDollarCircleLine, RiUserLine, RiArrowDownSLine } from "react-icons/ri";
import styles from "../FlightDetails/FlightDetails2.module.css"; 

const ReviewFlight2 = ({ mode, onNavigate, templateId, params: builderParams, styleConfig, branding, companyId }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const { user, loading: authLoading } = useAuth();

  // 🚀 HYBRID SAAS LOGIC: Identifying the Agency
  const finalCompanyId = (mode === "builder" || mode === "preview")
    ? (user?.isSupplierAPI === true ? user?.cid : user?.parentCompanyId)
    : companyId;

  const themeVariables = {
    "--primary-color": styleConfig?.primary || "#c9a84c",
    "--secondary-color": styleConfig?.secondary || "#e8cc7a",
  };

  const pageState = mode === "builder" ? builderParams : location.state;
  const flightResponse = pageState?.flightBookingResponse;

  /* ── State ── */
  const [flightBookingRequest, setFlightBookingRequest] = useState(null);
  const [itinerary, setItinerary] = useState([]);
  const [travellers, setTravellers] = useState([]);
  const [baseFare, setBaseFare] = useState(0);
  const [tax, setTax] = useState(0);
  const [totalPrice, setTotalPrice] = useState(0);
  const [convenienceFee, setConvenienceFee] = useState(0);
  const [paymentGatewayAmount, setPaymentGatewayAmount] = useState(0);
  const [btnLoading, setBtnLoading] = useState(false);
  const [openPanel, setOpenPanel] = useState(null);
  const [baggageSelections, setBaggageSelections] = useState({});
  const [mealSelections, setMealSelections] = useState({});

  useEffect(() => {
    if (flightResponse) {
      setFlightBookingRequest(flightResponse);
      setItinerary(flightResponse.dcFlightLegs || []);
      setTravellers(flightResponse.travellerInfos || []);
      setBaseFare(Number(flightResponse.baseFare) || 0);
      setTax(Number(flightResponse.tax) || 0);
      setConvenienceFee(Number(flightResponse.convenienceFee) || 0);
      setPaymentGatewayAmount(Number(flightResponse.paymentGatewayAmount) || 0);
      setTotalPrice(Number(flightResponse.totalPrice) || 0);
    }
  }, [flightResponse]);

  const handleAddItem = (type, travellerIndex, item) => {
    const key = travellerIndex + 1;
    if (type === "baggage") setBaggageSelections((prev) => ({ ...prev, [key]: item }));
    else if (type === "meal") setMealSelections((prev) => ({ ...prev, [key]: item }));
  };

  const totalBaggageAmount = Object.values(baggageSelections).reduce((sum, b) => sum + (Number(b?.amount) || 0), 0);
  const totalMealAmount = Object.values(mealSelections).reduce((sum, m) => sum + (Number(m?.amount) || 0), 0);

  useEffect(() => {
    if (!flightBookingRequest) return;
    const calculatedTotal = baseFare + tax + paymentGatewayAmount + convenienceFee + totalBaggageAmount + totalMealAmount;
    setTotalPrice(calculatedTotal);
  }, [baseFare, tax, paymentGatewayAmount, convenienceFee, totalBaggageAmount, totalMealAmount, flightBookingRequest]);

  /* ── Razorpay Helpers ── */
  function loadScript(src) {
    return new Promise((resolve) => {
      const script = document.createElement("script");
      script.src = src;
      script.onload = () => resolve(true);
      script.onerror = () => resolve(false);
      document.body.appendChild(script);
    });
  }

  const handleFinalBooking = async (paymentId, flightData) => {
    const finalRequest = {
      ...flightData,
      paymentStatus: "SUCCESS",
      paymentGatewayNo: paymentId,
      paymentGateway: "RAZORPAY",
      totalPrice: totalPrice,
      dcBaggageTypes: Object.keys(baggageSelections).map(key => ({
        travelerId: key,
        id: baggageSelections[key].id,
        name: baggageSelections[key].description,
        amount: baggageSelections[key].amount.toString()
      })),
      dcMealTypes: Object.keys(mealSelections).map(key => ({
        travelerId: key,
        id: mealSelections[key].id,
        name: mealSelections[key].description,
        amount: mealSelections[key].amount.toString()
      }))
    };

    const result = await apiService.post(config.BOOKFLIGHT, finalRequest);
    setBtnLoading(false);
    
    if (result) {
        navigate('/template/flight-confirmation', { state: { bookingData: result } });
    } else {
        alert("Payment successful but booking failed. Please contact support.");
    }
  };

  async function displayRazorpay() {
    setBtnLoading(true);
    const res = await loadScript("https://checkout.razorpay.com/v1/checkout.js");

    if (!res) {
      alert("Razorpay SDK failed to load. Check your internet.");
      setBtnLoading(false);
      return;
    }

    try {
      // Initialize payment on server
      const result = await apiService.post(
        `${config.MAKEPAYMENT}?Amount=${Math.round(totalPrice)}&PayRequest=RAZORPAY&CompanyId=${finalCompanyId}`
      );

      if (!result.succeeded) {
        alert("Payment initialization failed");
        setBtnLoading(false);
        return;
      }

      const { amount, id: order_id, currency, razorpayKey, name: brandName } = result.data;

      const options = {
        key: razorpayKey,
        amount,
        currency,
        name: brandName,
        description: "Flight Booking Fee",
        order_id,
        handler: (response) => handleFinalBooking(response.razorpay_payment_id, flightBookingRequest),
        prefill: {
          name: `${travellers[0]?.firstName} ${travellers[0]?.lastName}`,
          email: user?.email || travellers[0]?.email || "customer@travel.com",
          contact: user?.phoneNumber || "0000000000",
        },
        theme: { color: themeVariables["--primary-color"] },
        modal: { ondismiss: () => setBtnLoading(false) }
      };

      const paymentObject = new window.Razorpay(options);
      paymentObject.open();

    } catch (err) {
      alert("Something went wrong with payment initialization.");
      setBtnLoading(false);
    }
  }

  /* ── Main Submit ── */
  const handleSubmit = async (e) => {
    e.preventDefault();
    
    // 🚀 BUILDER & PREVIEW SIMULATION
    if (mode === "builder" || mode === "preview") {
      setBtnLoading(true);
      setTimeout(() => {
        const bookingData = { bookingId: "BK-88291", pnr: "PNR-READY", bookingStatus: "CONFIRMED", dcFlightLegs: itinerary, travellerInfos: travellers };
        setBtnLoading(false);
        if (mode === "builder") onNavigate({ name: "flight-confirmation", params: { bookingData } });
        else navigate(`/template-preview/${templateId}/flight-confirmation`, { state: { bookingData } });
      }, 1500);
      return;
    }

    // 🚀 LIVE MODE: TRIGGER RAZORPAY
    displayRazorpay();
  };

  if (!flightResponse && !authLoading) {
    return (
        <div className={styles.page} style={themeVariables}>
            <div style={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: '80vh' }}>
                <div style={{ textAlign: 'center', padding: '40px', background: 'rgba(255,255,255,0.03)', border: '1px solid rgba(255,255,255,0.1)', borderRadius: '20px' }}>
                    <RiAlertLine style={{ fontSize: '4rem', color: 'var(--primary-color)', marginBottom: '20px' }} />
                    <h2 style={{ color: '#fff', marginBottom: '10px' }}>Session Expired</h2>
                    <p style={{ color: 'rgba(255,255,255,0.6)', marginBottom: '20px' }}>Your booking session has expired.</p>
                    <button onClick={() => navigate(-1)} style={{ padding: '10px 24px', background: 'var(--primary-color)', color: '#0a1628', border: 'none', borderRadius: '30px', cursor: 'pointer', fontWeight: '600' }}>Go Back</button>
                </div>
            </div>
        </div>
    );
  }

  return (
    <div className={styles.page} style={themeVariables}>
      <div className={styles.main}>
        <div className={styles.leftCol}>
          
          {/* Journey Recap */}
          {itinerary.map((it, idx) => (
            it.dcDisplayLegs?.map((leg, lIdx) => (
              <div className={styles.flightCard} key={`${idx}-${lIdx}`}>
                  {/* Journey Cards remain as in your original design */}
              </div>
            ))
          ))}

          {/* Traveler Summary */}
          <div className={styles.sectionBlock}>
            <div className={styles.sectionHead}><RiUserLine className={styles.sectionHeadIcon} /><span className={styles.sectionTitle}>Traveler Summary</span></div>
            <div className={styles.sectionBody}>
              <table className={styles.baggageTable}>
                <thead><tr><th>No.</th><th>Name</th><th>Gender</th><th>Type</th></tr></thead>
                <tbody>
                  {travellers.map((t, index) => (
                    <tr key={index}>
                      <td>{index + 1}</td>
                      <td><strong>{t.title} {t.firstName} {t.lastName}</strong></td>
                      <td>{t.gender}</td>
                      <td>{t.travelerType}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>

          {/* Ancillaries (Baggage & Meals) */}
          <div className={styles.sectionBlock}>
            <div className={styles.sectionHead}><FaShoppingBag className={styles.sectionHeadIcon} /><span className={styles.sectionTitle}>Extra Baggage & Meals</span></div>
            <div className={styles.sectionBody}>
              {travellers.filter(t => t.travelerType !== 'INFANT').map((t, index) => (
                <div key={index} className={styles.passengerCard}>
                  <div className={styles.passengerCardTitle}>{t.title} {t.firstName} {t.lastName}</div>
                  
                  {/* Baggage */}
                  <button className={styles.btnSecondary} style={{width:'100%', marginBottom:10, textAlign:'left', display:'flex', justifyContent:'space-between', alignItems:'center'}} onClick={() => setOpenPanel(openPanel === `b-${index}` ? null : `b-${index}`)}>
                     <span style={{fontSize: '0.85rem'}}>🧳 Extra Baggage</span>
                     <div style={{display:'flex', alignItems:'center', gap: '10px'}}>
                        <span style={{color:'var(--primary-color)', fontSize:'0.75rem', fontWeight: 700}}>
                            {baggageSelections[index+1] ? baggageSelections[index+1].description : '+ Add'}
                        </span>
                        <RiArrowDownSLine style={{transform: openPanel === `b-${index}` ? 'rotate(180deg)' : 'none'}} />
                     </div>
                  </button>

                  {openPanel === `b-${index}` && (
                      <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:10, padding:'10px 0 20px'}}>
                          {itinerary[0]?.dcBaggageTypes?.length > 0 ? (
                            itinerary[0].dcBaggageTypes.map(bg => (
                              <div key={bg.id} onClick={() => handleAddItem("baggage", index, bg)} style={{padding:15, borderRadius:12, background:'rgba(255,255,255,0.05)', border: baggageSelections[index+1]?.id === bg.id ? '1.5px solid var(--primary-color)' : '1.5px solid rgba(255,255,255,0.1)', textAlign:'center', cursor:'pointer'}}>
                                  <div style={{fontSize:'0.75rem', color:'rgba(255,255,255,0.6)', marginBottom:5}}>{bg.description}</div>
                                  <div style={{color:'var(--primary-color)', fontWeight:800}}>₹{bg.amount}</div>
                              </div>
                            ))
                          ) : <p style={{fontSize:'0.8rem', opacity: 0.5}}>No baggage options found.</p>}
                      </div>
                  )}

                  {/* Meals */}
                  <button className={styles.btnSecondary} style={{width:'100%', textAlign:'left', display:'flex', justifyContent:'space-between', alignItems:'center'}} onClick={() => setOpenPanel(openPanel === `m-${index}` ? null : `m-${index}`)}>
                     <span style={{fontSize: '0.85rem'}}>🍱 Meal Options</span>
                     <div style={{display:'flex', alignItems:'center', gap: '10px'}}>
                        <span style={{color:'var(--primary-color)', fontSize:'0.75rem', fontWeight: 700}}>
                            {mealSelections[index+1] ? mealSelections[index+1].description : '+ Add'}
                        </span>
                        <RiArrowDownSLine style={{transform: openPanel === `m-${index}` ? 'rotate(180deg)' : 'none'}} />
                     </div>
                  </button>

                   {openPanel === `m-${index}` && (
                      <div style={{display:'grid', gridTemplateColumns:'repeat(auto-fit, minmax(140px, 1fr))', gap:10, padding:'10px 0 10px'}}>
                          {itinerary[0]?.dcMealTypes?.length > 0 ? (
                            itinerary[0].dcMealTypes.map(ml => (
                              <div key={ml.id} onClick={() => handleAddItem("meal", index, ml)} style={{padding:15, borderRadius:12, background:'rgba(255,255,255,0.05)', border: mealSelections[index+1]?.id === ml.id ? '1.5px solid var(--primary-color)' : '1.5px solid rgba(255,255,255,0.1)', textAlign:'center', cursor:'pointer'}}>
                                  <div style={{fontSize:'0.75rem', color:'rgba(255,255,255,0.6)', marginBottom:5}}>{ml.description}</div>
                                  <div style={{color:'var(--primary-color)', fontWeight:800}}>₹{ml.amount}</div>
                              </div>
                            ))
                          ) : <p style={{fontSize:'0.8rem', opacity: 0.5}}>No meal options found.</p>}
                      </div>
                  )}
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Payment Summary */}
        <div className={styles.rightCol}>
          <div className={styles.fareCard}>
            <div className={styles.fareCardHead}><RiMoneyDollarCircleLine className={styles.fareCardHeadIcon} /><span className={styles.fareCardTitle}>Payment Summary</span></div>
            <div className={styles.fareCardBody}>
              <div className={styles.fareRow}><span>Base Fare</span><span>INR {baseFare.toFixed(2)}</span></div>
              <div className={styles.fareRow}><span>Taxes & Fees</span><span>INR {(tax + convenienceFee).toFixed(2)}</span></div>
              {totalBaggageAmount > 0 && <div className={styles.fareRow}><span style={{color:'var(--primary-color)'}}>Extra Baggage</span><span style={{color:'var(--primary-color)'}}>+ INR {totalBaggageAmount}</span></div>}
              {totalMealAmount > 0 && <div className={styles.fareRow}><span style={{color:'var(--primary-color)'}}>Extra Meals</span><span style={{color:'var(--primary-color)'}}>+ INR {totalMealAmount}</span></div>}
              <div className={styles.fareTotalBlock}>
                <span className={styles.fareTotalLabel}>Total Amount</span>
                <span className={styles.fareTotalAmount}>INR {Math.round(totalPrice)}</span>
              </div>
              <button className={styles.btnPrimary} style={{ width: '100%', marginTop: '20px' }} onClick={handleSubmit} disabled={btnLoading}>
                {btnLoading ? 'Processing...' : `Pay INR ${Math.round(totalPrice)}`}
              </button>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ReviewFlight2;